import {Component, OnInit, OnDestroy} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {TargetGroup} from 'shared/models/target-groups/target-group';
import {TargetGroupAnalytics} from '../../models/target-group-analytics.model';
import {TargetGroupAnalyticsService} from '../../services/target-group-analytics.service';

// Excluded target group titles (dummy/test groups)
const EXCLUDED_TARGET_GROUPS = ['KursImpulseDummy', 'FragebogenDummy'];

@Component({
  selector: 'leada-target-group-analytics',
  templateUrl: './target-group-analytics.component.html',
  styleUrls: ['./target-group-analytics.component.css']
})
export class TargetGroupAnalyticsComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];

  // Available target groups
  public targetGroups: Array<TargetGroup> = [];

  // Selection state
  public selectedTargetGroup: TargetGroup | string;
  public selectedTargetGroups: Array<TargetGroup> = [];

  // Date range
  public startDate: Date;
  public endDate: Date;
  private startDateISO: string;
  private endDateISO: string;
  public useThreeMonthDefault: boolean = true;

  // Results
  public analytics: TargetGroupAnalytics | null = null;
  public isLoading: boolean = false;
  public isLoadingTargetGroups: boolean = false;
  public errorMessage: string = '';

  constructor(
    private analyticsService: TargetGroupAnalyticsService
  ) {}

  ngOnInit(): void {
    // Set default to 3 months
    this.endDate = new Date();
    this.startDate = new Date();
    this.startDate.setMonth(this.startDate.getMonth() - 3);
    this.updateDateISO();

    // Load target groups
    this.isLoadingTargetGroups = true;
    const targetGroupsSub = this.analyticsService.getAllTargetGroups().subscribe(
      (targetGroups: Array<TargetGroup>) => {
        this.targetGroups = targetGroups.filter(
          tg => !EXCLUDED_TARGET_GROUPS.includes(tg.title)
        );
        this.isLoadingTargetGroups = false;
      },
      error => {
        this.errorMessage = 'Fehler beim Laden der Zielgruppen';
        this.isLoadingTargetGroups = false;
        console.error(error);
      }
    );
    this.subscriptions.push(targetGroupsSub);
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  selectTargetGroup(targetGroup: TargetGroup | string): void {
    if (typeof targetGroup === 'string') {
      return;
    }

    const index = this.targetGroups.indexOf(targetGroup);
    if (index > -1) {
      this.selectedTargetGroups.push(targetGroup);
      this.targetGroups.splice(index, 1);
      this.analytics = null; // Clear old results when selection changes
    }
    this.selectedTargetGroup = '';
  }

  deselectTargetGroup(targetGroup: TargetGroup): void {
    const index = this.selectedTargetGroups.indexOf(targetGroup);
    if (index > -1) {
      this.targetGroups.push(targetGroup);
      this.selectedTargetGroups.splice(index, 1);
      this.analytics = null; // Clear old results when selection changes
    }
  }

  handleStartDateChange(date: Date): void {
    this.startDate = date;
    this.updateDateISO();
    this.validateDates();
    this.analytics = null; // Clear old results when date changes
  }

  handleEndDateChange(date: Date): void {
    this.endDate = date;
    this.updateDateISO();
    this.validateDates();
    this.analytics = null; // Clear old results when date changes
  }

  private validateDates(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    // Check if start date is after end date
    if (this.startDate > this.endDate) {
      this.errorMessage = 'Das Startdatum muss vor dem Enddatum liegen';
      return;
    }

    // Check if dates are in the future
    if (this.startDate > today || this.endDate > today) {
      this.errorMessage = 'Das Datum darf nicht in der Zukunft liegen';
      return;
    }

    // Clear error if validation passes
    if (this.errorMessage === 'Das Startdatum muss vor dem Enddatum liegen' ||
        this.errorMessage === 'Das Datum darf nicht in der Zukunft liegen') {
      this.errorMessage = '';
    }
  }

  toggleThreeMonthDefault(): void {
    if (this.useThreeMonthDefault) {
      // When activating: reset to 3 months
      this.endDate = new Date();
      this.startDate = new Date();
      this.startDate.setMonth(this.startDate.getMonth() - 3);
      this.updateDateISO();
      this.validateDates();
      this.analytics = null; // Clear old results when date changes
    }
    // When deactivating: keep current values, datepickers become editable
  }

  private updateDateISO(): void {
    this.startDateISO = this.startDate.toISOString().split('T')[0];
    this.endDateISO = this.endDate.toISOString().split('T')[0];
  }

  loadAnalytics(): void {
    if (this.selectedTargetGroups.length === 0) {
      this.errorMessage = 'Bitte wählen Sie mindestens eine Zielgruppe aus';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.analytics = null;

    const targetGroupIds = this.selectedTargetGroups.map(tg => tg._id);

    const analyticsSub = this.analyticsService.getAnalytics(
      this.startDateISO,
      this.endDateISO,
      targetGroupIds
    ).subscribe(
      (result: TargetGroupAnalytics) => {
        // Validate response structure
        if (this.isValidAnalyticsResponse(result)) {
          this.analytics = result;
        } else {
          this.errorMessage = 'Ungültige Daten vom Server erhalten';
          console.error('Invalid analytics response:', result);
        }
        this.isLoading = false;
      },
      error => {
        this.errorMessage = 'Fehler beim Laden der Auswertung';
        this.isLoading = false;
        console.error(error);
      }
    );
    this.subscriptions.push(analyticsSub);
  }

  private isValidAnalyticsResponse(data: any): boolean {
    return data &&
           data.period && data.period.from && data.period.to &&
           data.users && typeof data.users.registered === 'number' &&
           data.cmodules && typeof data.cmodules.available === 'number' &&
           data.tips && typeof data.tips.available === 'number' &&
           data.podcasts && typeof data.podcasts.usersWhoViewedPodcasts === 'number';
  }

  exportAsCSV(): void {
    if (!this.analytics) {
      return;
    }

    // Convert analytics to CSV format with UTF-8 BOM for Excel compatibility
    let csv = '\ufeff'; // UTF-8 BOM
    csv += 'Nutzergruppen-Auswertung\n\n';
    csv += `Zeitraum:,${this.analytics.period.from} bis ${this.analytics.period.to}\n`;
    csv += `Zielgruppen:,${this.analytics.targetGroups.map(tg => tg.title).join(', ')}\n\n`;
    csv += 'BENUTZERSTATISTIK\n';
    csv += `Angemeldete Nutzer,${this.analytics.users.registered}\n`;
    csv += `Aktive Nutzer,${this.analytics.users.active},${this.analytics.users.activePercentage}%\n`;
    csv += `Durchschn. Logins pro Woche,${this.analytics.users.avgLoginsPerWeek}\n\n`;
    csv += 'THEMENPAKETE\n';
    csv += `Verfügbare Themenpakete,${this.analytics.cmodules.available}\n`;
    csv += `Genutzte Themenpakete,${this.analytics.cmodules.used},${this.analytics.cmodules.usedPercentage}%\n`;
    csv += `Abgeschlossene Themenpakete,${this.analytics.cmodules.completed},${this.analytics.cmodules.completedPercentage}%\n\n`;
    csv += 'TIPPS & PODCASTS\n';
    csv += `Verfügbare Tipps,${this.analytics.tips.available}\n`;
    csv += `Genutzte Tipps,${this.analytics.tips.used},${this.analytics.tips.usedPercentage}%\n`;
    csv += `Podcast-Nutzer,${this.analytics.podcasts.usersWhoViewedPodcasts},${this.analytics.podcasts.podcastUsagePercentage}%\n`;

    // Trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Nutzergruppen_Auswertung_${this.startDateISO}_${this.endDateISO}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  isDisabled(): boolean {
    return !this.startDateISO ||
           !this.endDateISO ||
           this.selectedTargetGroups.length === 0 ||
           this.isLoading ||
           (this.errorMessage !== '' && this.errorMessage !== 'Bitte wählen Sie mindestens eine Zielgruppe aus' && this.errorMessage !== 'Fehler beim Laden der Zielgruppen' && this.errorMessage !== 'Fehler beim Laden der Auswertung');
  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/export/components/target-group-analytics/target-group-analytics.component.ts